import { Box, Typography, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FC } from 'react';
import { List, RowComponentProps } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';

import { Company } from '@apiContract';

import { sxList, sxNoResult } from '../styles';
import Item from './Item';

type Props = {
  loadNextPage: (startIndex: number, stopIndex: number) => void;
  isNextPageLoading: boolean;
  items: Array<Company>;
  setSelectedItem: (company: Company) => void;
  selectedItem?: Company;
  hasNextPage: boolean;
};

const CompanyItem = ({
  style,
  index,
  selectedItem,
  items,
  setSelectedItem,
}: RowComponentProps<{
  selectedItem: Company | undefined;
  items: Company[];
  setSelectedItem: (company: Company) => void;
}>) => (
  <Item
    style={style}
    selected={!!selectedItem && !!items[index] && items[index]?.id === selectedItem?.id}
    item={items[index]}
    handleClick={() => items[index] && setSelectedItem(items[index] as Company)}
  />
);

const CompanyList: FC<Props> = ({
  loadNextPage,
  isNextPageLoading,
  items,
  setSelectedItem,
  selectedItem,
  hasNextPage,
}) => {
  const theme = useTheme();
  const isSmallHeight = useMediaQuery('(max-height: 500px)');

  const rowCount = hasNextPage ? items.length + 1 : items.length;
  const isRowLoaded = (index: number) => !hasNextPage || index < items.length;
  const onItemsRendered = useInfiniteLoader({
    isRowLoaded,
    rowCount,
    loadMoreRows: loadNextPage as unknown as (startIndex: number, stopIndex: number) => Promise<void>,
  });

  return (
    <Box sx={{ ...sxList(theme) }}>
      <List
        onRowsRendered={onItemsRendered}
        rowCount={rowCount}
        rowHeight={66}
        style={{ height: isSmallHeight ? 200 : 315, width: 368 }}
        rowProps={{
          selectedItem,
          items,
          setSelectedItem,
        }}
        className="companiesScrollBar"
        rowComponent={CompanyItem}
      />
      {items.length === 0 && !isNextPageLoading ? (
        <Typography sx={sxNoResult} variant="body2">
          No results found
        </Typography>
      ) : null}
    </Box>
  );
};

export default CompanyList;
